/* Cycling Upgrades — blueprint motion scenes for collection headers.
   Each scene is a pure function of time: draw(ctx, t, W, H) paints one frame in CSS pixels and
   the same t always gives the same picture. Scenes play once (about 4s, under the WCAG 2.2.2
   five-second limit) and hold on their finished diagram; cu.js runs them on
   <canvas data-cu-motion="name">, reduced motion gets the finished frame straight away.
   The page behind the canvas supplies the slate blueprint, so scenes only draw linework.
   Every scene is designed in a 600 x 150 box whose content sits in x 200–600, fitted to the
   right of the banner so the collection title on the left stays clear.
   Preview any scene from motion/scenes/index.html?scene=<name> in the repo. */
(() => {
  const BONE = '#e7e6dd';
  const ORANGE = '#f26a21';
  const SLATE = '#1f2b35';
  const PILL = 'rgba(31, 43, 53, 0.92)';
  const PILL_LINE = 'rgba(231, 230, 221, 0.32)';
  const DISPLAY = "'Special Gothic Condensed One', 'Arial Narrow', sans-serif";
  const TAU = Math.PI * 2;
  const DURATION = 4;

  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const easeOut = (x) => 1 - Math.pow(1 - clamp(x), 3);
  const easeInOut = (x) => { x = clamp(x); return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2; };
  const P = (t, a, b, f = easeOut) => f(clamp((t - a) / (b - a)));

  /* ---------- Drawing kit (all sizes in design units unless named px) ---------- */
  function kit(ctx, k) {
    const px = (n) => n / k;
    const ts = clamp(k, 0.72, 1); // type shrinks with small drawings, but stays legible
    const lw = (n) => { ctx.lineWidth = px(n); };
    const font = (n) => { ctx.font = `${px(n * ts)}px ${DISPLAY}`; };
    const alpha = (a) => { ctx.globalAlpha = clamp(a); };
    const line = (x1, y1, x2, y2, p = 1) => {
      if (p <= 0) return;
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x1 + (x2 - x1) * p, y1 + (y2 - y1) * p); ctx.stroke();
    };
    const poly = (pts, p = 1) => { // partial polyline, by length
      if (p <= 0 || pts.length < 2) return;
      let total = 0;
      for (let i = 1; i < pts.length; i++) total += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
      let left = total * clamp(p);
      ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length && left > 0; i++) {
        const [ax, ay] = pts[i - 1]; const [bx, by] = pts[i];
        const d = Math.hypot(bx - ax, by - ay);
        const f = Math.min(1, left / d);
        ctx.lineTo(ax + (bx - ax) * f, ay + (by - ay) * f);
        left -= d;
      }
      ctx.stroke();
    };
    const ring = (cx, cy, r, p = 1, a0 = -Math.PI / 2) => {
      if (p <= 0) return;
      ctx.beginPath(); ctx.arc(cx, cy, r, a0, a0 + TAU * clamp(p)); ctx.stroke();
    };
    const dot = (cx, cy, rpx, color = ORANGE) => { ctx.fillStyle = color; ctx.beginPath(); ctx.arc(cx, cy, px(rpx), 0, TAU); ctx.fill(); ctx.fillStyle = BONE; };
    // A tube drawn as a hollow outline, like the exploded bike: wide bone stroke, slate core
    const tube = (pts, w, p = 1) => {
      if (p <= 0) return;
      ctx.save(); ctx.lineWidth = w + px(3); ctx.strokeStyle = BONE; poly(pts, p);
      ctx.lineWidth = w; ctx.strokeStyle = SLATE; poly(pts, p); ctx.restore();
    };
    // Dimension line grown from its middle, with arrowheads; optional extension lines
    const dim = (x1, y1, x2, y2, p, color = BONE) => {
      if (p <= 0) return;
      const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
      const ax = mx + (x1 - mx) * p, ay = my + (y1 - my) * p;
      const bx = mx + (x2 - mx) * p, by = my + (y2 - my) * p;
      ctx.save(); ctx.strokeStyle = color; lw(1);
      ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.stroke();
      const ang = Math.atan2(by - ay, bx - ax), h = px(7), s = px(3.5);
      [[ax, ay, ang + Math.PI], [bx, by, ang]].forEach(([x, y, a]) => {
        ctx.beginPath();
        ctx.moveTo(x - Math.cos(a) * h - Math.sin(a) * s, y - Math.sin(a) * h + Math.cos(a) * s);
        ctx.lineTo(x, y);
        ctx.lineTo(x - Math.cos(a) * h + Math.sin(a) * s, y - Math.sin(a) * h - Math.cos(a) * s);
        ctx.stroke();
      });
      ctx.restore();
    };
    const ext = (x1, y1, x2, y2, p) => { ctx.save(); lw(1); alpha(0.6 * clamp(p)); ctx.setLineDash([px(3), px(3)]); line(x1, y1, x2, y2); ctx.restore(); };
    // Text with a slate knock-out behind it so lines don't run through the letters
    const label = (x, y, text, a, sizePx = 15, align = 'center') => {
      if (a <= 0) return;
      ctx.save(); font(sizePx); ctx.textAlign = align; ctx.textBaseline = 'middle';
      const w = ctx.measureText(text).width, padX = px(5 * ts), h = px((sizePx + 4) * ts);
      const x0 = align === 'center' ? x - w / 2 : align === 'right' ? x - w : x;
      alpha(a); ctx.fillStyle = SLATE; ctx.fillRect(x0 - padX, y - h / 2, w + padX * 2, h);
      ctx.fillStyle = BONE; ctx.fillText(text, x, y + px(1));
      ctx.restore();
    };
    // Pill tag like the Shop by part labels; x is its left edge (or right edge when align='right')
    const tag = (x, y, text, a, align = 'left') => {
      if (a <= 0) return;
      ctx.save(); font(16); ctx.textBaseline = 'middle'; ctx.textAlign = 'center';
      const w = ctx.measureText(text).width + px(20 * ts), h = px(28 * ts);
      const x0 = align === 'left' ? x : x - w;
      alpha(a);
      ctx.fillStyle = PILL; ctx.strokeStyle = PILL_LINE; lw(1);
      ctx.beginPath(); ctx.roundRect(x0, y - h / 2, w, h, px(6)); ctx.fill(); ctx.stroke();
      ctx.fillStyle = BONE; ctx.fillText(text, x0 + w / 2, y + px(1));
      ctx.restore();
      return w;
    };
    return { px, lw, font, alpha, line, poly, ring, dot, tube, dim, ext, label, tag };
  }

  /* Fit the 600 x 150 design box to the right of the banner; returns the kit, the scale and
     the design-x of the canvas's left edge (for things that roll in from off-screen). */
  function stage(ctx, W, H, paint, t) {
    ctx.clearRect(0, 0, W, H);
    // Wide: beside the title, vertically centred. Narrow (phones): smaller, top-right, above
    // the title block (breadcrumb + heading take the bottom ~90px).
    const margin = Math.max(16, W * 0.04);
    const narrow = W < 700;
    const k = narrow
      ? Math.min((H - 92) / 150, (W - margin * 2) / 400)
      : Math.min((H - Math.max(14, H * 0.08) * 2) / 150, (W - margin - W * 0.42) / 400);
    const tx = W - margin - 600 * k, ty = narrow ? 10 : (H - 150 * k) / 2;
    ctx.save();
    ctx.translate(tx, ty); ctx.scale(k, k);
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.strokeStyle = BONE; ctx.fillStyle = BONE;
    const K = kit(ctx, k);
    K.lw(1.4);
    paint(ctx, clamp(t, 0, DURATION), K, { k, left: -tx / k, right: (W - tx) / k });
    ctx.restore();
  }
  const scene = (paint) => ({ duration: DURATION, draw: (ctx, t, W, H) => stage(ctx, W, H, paint, t) });

  /* Wheel shared by the road and MTB scenes, at the exploded bike's proportions
     (tyre r190, rim edge r176, spoke bed r146, rotor r46, hub r20). */
  function wheel(ctx, K, cx, cy, R, spin, knobby = false, p = 1) {
    const s = R / 190;
    ctx.save(); ctx.translate(cx, cy);
    K.alpha(0.07 * p); ctx.lineWidth = 30 * s; K.ring(0, 0, 161 * s, p);
    K.alpha(1); K.lw(1.4);
    K.ring(0, 0, 190 * s, p); K.ring(0, 0, 176 * s, p); K.ring(0, 0, 146 * s, p);
    ctx.rotate(spin);
    if (knobby) {
      K.lw(2.2);
      for (let i = 0; i < 32; i++) { const a = (i * TAU) / 32; K.line(Math.cos(a) * 190 * s, Math.sin(a) * 190 * s, Math.cos(a) * 202 * s, Math.sin(a) * 202 * s); }
    }
    K.alpha(0.8 * p); K.lw(0.8);
    for (let i = 0; i < 24; i++) {
      const a = (i * TAU) / 24, b = a + (i % 2 ? 0.2 : -0.1);
      K.line(Math.cos(a) * 17 * s, Math.sin(a) * 17 * s, Math.cos(b) * 146 * s, Math.sin(b) * 146 * s);
    }
    K.ring(0, 0, 38 * s);
    K.alpha(p); K.lw(1.4); K.ring(0, 0, 46 * s); K.ring(0, 0, 20 * s);
    ctx.restore();
  }
  function ground(ctx, K, left, right, y) { ctx.save(); K.alpha(0.16); K.lw(1); K.line(left, y, right, y); ctx.restore(); }
  function tread(ctx, K, from, to, y, fade) {
    ctx.save(); K.lw(2);
    for (let x = from; x < to; x += 14) { K.alpha(0.45 * clamp(1 - (to - x) / fade)); K.line(x, y + 4, Math.min(x + 6, to), y + 4); }
    ctx.restore();
  }

  const scenes = {
    /* 1. Road wheel: rolls in and stops; the ISO bead-seat diameter (622 mm for 700c) is marked
       on the rim where the tyre bead sits, not across the tyre. */
    wheel: scene((ctx, t, K, { left, right }) => {
      const R = 58, s = R / 190, gy = 146, cy = gy - R, fx = 400, x0 = left - R - 10;
      const cx = x0 + (fx - x0) * P(t, 0, 2.4);
      ground(ctx, K, left, right, gy);
      tread(ctx, K, x0 + R, cx, gy, 240);
      wheel(ctx, K, cx, cy, R, (cx - x0) / R);
      K.dot(cx, cy, 4);
      const bsd = 172 * s, pb = P(t, 2.3, 3.0), dimY = 14;
      ctx.save(); ctx.strokeStyle = ORANGE; K.lw(1.6); ctx.setLineDash([K.px(4), K.px(3)]); K.ring(cx, cy, bsd, pb); ctx.restore();
      K.ext(cx - bsd, cy, cx - bsd, dimY - 5, pb); K.ext(cx + bsd, cy, cx + bsd, dimY - 5, pb);
      K.dim(cx - bsd, dimY, cx + bsd, dimY, P(t, 2.6, 3.3), ORANGE);
      K.label(cx, dimY, 'Ø 622 BSD', P(t, 3.0, 3.4), 14);
      const pt = P(t, 3.1, 3.7);
      ctx.save(); K.lw(1); K.alpha(pt); K.line(cx + R + 2, cy, cx + R + 16, cy, pt); ctx.restore();
      K.tag(cx + R + 16, cy, '700C · 25-622', pt);
    }),

    /* 2. MTB trail: a 29er front end rolls over a rock; the fork compresses and rebounds. */
    mtb: scene((ctx, t, K, { left, right }) => {
      const R = 48, s = R / 190, gy = 146, fx = 455, x0 = left - R - 70, rock = 330;
      const cx = x0 + (fx - x0) * P(t, 0, 3.0);
      const lift = Math.abs(cx - rock) < 55 ? 15 * Math.cos((Math.PI * (cx - rock)) / 110) ** 2 : 0;
      const cy = gy - 202 * s - lift;
      const u = (cx - (rock - 55)) / 100;
      const comp = u < 0 ? 0 : u < 1 ? 13 * Math.sin(Math.PI * u) : 3.5 * Math.sin(TAU * (u - 1) * 1.4) * Math.exp(-3 * (u - 1));
      ground(ctx, K, left, rock - 36, gy); ground(ctx, K, rock + 36, right, gy);
      ctx.save(); K.alpha(0.5); K.poly(Array.from({ length: 19 }, (_, i) => { const x = rock - 36 + i * 4; return [x, gy - 16 * Math.cos((Math.PI * (x - rock)) / 72) ** 2]; })); ctx.restore();
      tread(ctx, K, x0 + R, Math.min(cx, rock - 40), gy, 200);
      wheel(ctx, K, cx, cy, R, (cx - x0) / (202 * s), true);
      // fork: lowers from the axle, stanchions above (shorter as it compresses), crown, steerer
      const dx = -Math.cos((66 * Math.PI) / 180), dy = -Math.sin((66 * Math.PI) / 180), nx = -dy, ny = dx;
      const at = (d, o) => [cx + dx * d + nx * o, cy + dy * d + ny * o];
      const low = 44, stan = 34 - comp, crown = low + stan;
      [-4.5, 4.5].forEach((o) => { K.poly([at(0, o), at(low, o)]); });
      K.lw(1); [-3.2, 3.2].forEach((o) => { K.poly([at(low - 6, o), at(crown, o)]); }); K.lw(1.4);
      K.poly([at(crown, -9), at(crown, 9)]);
      K.poly([at(crown, 0), at(crown + 16, 0)]);
      K.dot(cx, cy, 4);
      const pc = P(t, 3.0, 3.6);
      const [ax, ay] = at(low, -16), [bx, by] = at(low + 34, -16);
      K.dim(ax, ay, bx, by, pc, ORANGE);
      K.label(bx - 34, by + 6, '120 MM', P(t, 3.2, 3.6), 14, 'right');
      const pt = P(t, 3.2, 3.8);
      ctx.save(); K.lw(1); K.alpha(pt); K.line(cx + R + 4, cy, cx + R + 16, cy, pt); ctx.restore();
      K.tag(cx + R + 16, cy, '29 · 622', pt);
    }),

    /* 3. Drivetrain: chain wraps chainring and cassette; the derailleur shifts down the cogs. */
    drivetrain: scene((ctx, t, K) => {
      const c1 = [250, 78], r1 = 46, c2 = [462, 78];
      const teeth = [11, 13, 15, 17, 19, 21, 24, 27, 30, 34], rOf = (T) => (46 * T) / 50;
      // active cog: 34T, then shifts to 24T and 17T
      const shifts = [[1.9, 34, 24], [2.5, 24, 17]];
      let rc = rOf(34);
      shifts.forEach(([at, a, b]) => { if (t >= at) rc = rOf(a) + (rOf(b) - rOf(a)) * P(t, at, at + 0.3); });
      K.ring(c1[0], c1[1], r1, P(t, 0, 0.9));
      ctx.save(); K.lw(4); ctx.setLineDash([K.px(2.2), K.px(2.2)]); K.alpha(0.8 * P(t, 0.5, 1.1)); K.ring(c1[0], c1[1], r1 + 3); ctx.restore();
      K.ring(c1[0], c1[1], 12, P(t, 0.2, 0.9));
      ctx.save(); K.alpha(P(t, 0.3, 0.9)); K.ring(c1[0], c1[1], 36);
      for (let i = 0; i < 5; i++) { const b = (i * TAU) / 5 + 0.3; K.line(c1[0] + Math.cos(b) * 12, c1[1] + Math.sin(b) * 12, c1[0] + Math.cos(b) * 36, c1[1] + Math.sin(b) * 36); }
      K.poly([[c1[0] - 6, c1[1] + 3], [c1[0] - 44, c1[1] + 52], [c1[0] - 38, c1[1] + 56], [c1[0] + 4, c1[1] + 8]]);
      ctx.restore();
      ctx.save(); K.lw(0.9);
      teeth.forEach((T, i) => { K.alpha(0.65); K.ring(c2[0], c2[1], rOf(T), P(t, 0.15 + i * 0.06, 0.8 + i * 0.06)); });
      ctx.restore();
      ctx.save(); ctx.strokeStyle = ORANGE; K.lw(1.8); K.ring(c2[0], c2[1], rc, P(t, 0.9, 1.3)); ctx.restore();
      // chain: top run is the outer tangent; bottom run goes through the derailleur jockeys
      const pc = P(t, 1.0, 1.5);
      if (pc > 0) {
        const d = c2[0] - c1[0], phi = Math.asin((r1 - rc) / d);
        const n = [Math.sin(phi), -Math.cos(phi)];
        const j1 = [c2[0] - 2, c2[1] + rc + 12];
        const cage = 0.5 + (rc - rOf(11)) / 40, j2 = [j1[0] - Math.sin(cage) * 30, j1[1] + Math.cos(cage) * 30];
        ctx.save(); K.lw(3); K.alpha(0.75 * pc);
        ctx.setLineDash([K.px(3), K.px(2)]); ctx.lineDashOffset = -K.px(60) * P(t, 1.2, 3.0, (x) => x);
        K.poly([[c1[0] + r1 * n[0], c1[1] + r1 * n[1]], [c2[0] + rc * n[0], c2[1] + rc * n[1]]]);
        K.poly([[c2[0], c2[1] + rc], [j1[0], j1[1] - 6], [j2[0], j2[1] + 6], [c1[0], c1[1] + r1]]);
        ctx.restore();
        K.alpha(pc); K.ring(j1[0], j1[1], 6); K.ring(j2[0], j2[1], 6); K.line(j1[0], j1[1], j2[0], j2[1]); K.alpha(1);
      }
      const pd = P(t, 3.0, 3.6), x = c2[0] + rOf(34) + 14;
      K.ext(c2[0], c2[1] - rOf(11), x + 4, c2[1] - rOf(11), pd); K.ext(c2[0], c2[1] - rOf(34), x + 4, c2[1] - rOf(34), pd);
      K.dim(x, c2[1] - rOf(11), x, c2[1] - rOf(34), pd, ORANGE);
      K.label(x + 8, c2[1] - (rOf(11) + rOf(34)) / 2, '11–34T', P(t, 3.2, 3.6), 15, 'left');
    }),

    /* 4. Crank and power: one revolution to level, power counts up; crank length marked. */
    crank: scene((ctx, t, K) => {
      const c = [392, 76], arm = 72;
      const turn = P(t, 0.5, 3.0, easeInOut);
      const a = -Math.PI / 2 + (TAU + Math.PI / 2) * turn;
      K.ring(c[0], c[1], 50, P(t, 0, 0.9));
      ctx.save(); K.lw(4); ctx.setLineDash([K.px(2.2), K.px(2.2)]); K.alpha(0.8 * P(t, 0.4, 1.0)); K.ring(c[0], c[1], 53); ctx.restore();
      ctx.save(); ctx.translate(c[0], c[1]); ctx.rotate(a);
      K.alpha(P(t, 0.3, 0.8));
      for (let i = 0; i < 5; i++) { const b = (i * TAU) / 5 + 0.6; K.line(Math.cos(b) * 13, Math.sin(b) * 13, Math.cos(b) * 40, Math.sin(b) * 40); }
      K.ring(0, 0, 40); K.ring(0, 0, 13);
      // arm, tapering to the pedal eye, and the pedal
      K.poly([[0, -8], [arm, -5], [arm, 5], [0, 8]]);
      K.ring(arm, 0, 6); K.ring(0, 0, 5);
      ctx.restore();
      const ex = c[0] + Math.cos(a) * arm, ey = c[1] + Math.sin(a) * arm;
      ctx.save(); K.alpha(P(t, 0.3, 0.8)); ctx.beginPath(); ctx.roundRect(ex - 10, ey - 3, 20, 6, 2); ctx.stroke(); ctx.restore();
      K.dot(c[0], c[1], 3.5);
      // power readout
      const watts = Math.round(312 * P(t, 0.5, 3.0, easeInOut));
      ctx.save(); K.alpha(P(t, 0.4, 0.9)); ctx.textBaseline = 'alphabetic'; ctx.textAlign = 'left';
      K.font(13); ctx.fillStyle = 'rgba(231, 230, 221, 0.7)'; ctx.fillText('POWER', 500, 52);
      K.font(46); ctx.fillStyle = BONE; ctx.fillText(String(watts), 500, 94);
      const w = ctx.measureText(String(watts)).width; K.font(18); ctx.fillStyle = ORANGE; ctx.fillText('W', 500 + w + K.px(4), 94);
      ctx.restore();
      const pd = P(t, 3.0, 3.6), y = c[1] + 18;
      K.ext(c[0], c[1] + 6, c[0], y + 4, pd); K.ext(c[0] + arm, c[1] + 6, c[0] + arm, y + 4, pd);
      K.dim(c[0], y, c[0] + arm, y, pd, ORANGE);
      K.label(c[0] + arm / 2, y + 16, '172.5 MM', P(t, 3.2, 3.6), 14);
    }),

    /* 5. Cockpit, from above: bar draws out from the clamp, stem extends to the steerer. */
    cockpit: scene((ctx, t, K) => {
      const cx = 420, by = 40, half = 90;
      const pb = P(t, 0, 1.2);
      const side = (sgn) => [[cx, by], [cx + sgn * (half - 24), by], [cx + sgn * (half - 8), by + 4], [cx + sgn * half, by + 14], [cx + sgn * (half - 2), by + 34]];
      K.poly(side(-1), pb); K.poly(side(1), pb);
      K.poly(side(-1).map(([x, y]) => [x, y - 6]).slice(0, 3), pb); K.poly(side(1).map(([x, y]) => [x, y - 6]).slice(0, 3), pb);
      const ph = P(t, 1.0, 1.5);
      ctx.save(); K.alpha(ph); [-1, 1].forEach((sgn) => { ctx.beginPath(); ctx.roundRect(cx + sgn * (half - 4) - 6, by + 2, 12, 26, 5); ctx.stroke(); }); ctx.restore();
      const L = 34 + 36 * P(t, 1.2, 2.6);
      const ps = P(t, 1.1, 1.5);
      ctx.save(); K.alpha(ps);
      ctx.beginPath(); ctx.roundRect(cx - 13, by - 9, 26, 12, 3); ctx.stroke();
      K.line(cx - 6, by + 3, cx - 6, by + L - 9); K.line(cx + 6, by + 3, cx + 6, by + L - 9);
      K.ring(cx, by + L, 10); K.ring(cx, by + L, 4);
      ctx.restore();
      K.dot(cx, by + L, 3);
      const pw = P(t, 3.0, 3.6), wy = 14;
      K.ext(cx - half, by - 2, cx - half, wy - 4, pw); K.ext(cx + half, by - 2, cx + half, wy - 4, pw);
      K.dim(cx - half, wy, cx + half, wy, pw, ORANGE);
      K.label(cx, wy, '420 MM', P(t, 3.2, 3.6), 14);
      const sx = cx + 28;
      K.ext(cx + 14, by - 3, sx + 4, by - 3, P(t, 3.1, 3.7)); K.ext(cx + 10, by + L, sx + 4, by + L, P(t, 3.1, 3.7));
      K.dim(sx, by - 3, sx, by + L, P(t, 3.1, 3.7));
      K.label(sx + 8, by + L / 2, '100 MM · −6°', P(t, 3.3, 3.7), 14, 'left');
    }),

    /* 6. Saddle and post: the post rises to height, the saddle slides back to its setback. */
    saddle: scene((ctx, t, K) => {
      const ang = (73 * Math.PI) / 180, dx = -Math.cos(ang), dy = -Math.sin(ang), nx = -dy, ny = dx;
      const B = [470, 152], T = [B[0] + dx * 62, B[1] + dy * 62];
      const at = (p0, d, o) => [p0[0] + dx * d + nx * o, p0[1] + dy * d + ny * o];
      const pt = P(t, 0, 0.8);
      K.tube([B, T], 11, pt);
      const ext = 18 + 30 * P(t, 0.9, 2.2);
      const C = at(T, ext, 0);
      const pp = P(t, 0.6, 1.0);
      ctx.save(); K.alpha(pp); K.lw(1.2);
      K.poly([at(T, -4, -4.5), at(T, ext, -4.5)]); K.poly([at(T, -4, 4.5), at(T, ext, 4.5)]);
      ctx.beginPath(); ctx.roundRect(C[0] - 10, C[1] - 6, 20, 7, 2); ctx.stroke();
      ctx.restore();
      const back = 14 * P(t, 2.2, 2.9);
      const sx = C[0] - back, sy = C[1] - 6;
      const ps = P(t, 0.8, 1.4);
      ctx.save(); K.alpha(ps);
      K.line(sx - 30, sy, sx + 34, sy); // rails
      ctx.beginPath();
      ctx.moveTo(sx - 58, sy - 14);
      ctx.quadraticCurveTo(sx - 52, sy - 24, sx - 26, sy - 20);
      ctx.quadraticCurveTo(sx + 20, sy - 14, sx + 72, sy - 12);
      ctx.quadraticCurveTo(sx + 76, sy - 9, sx + 66, sy - 7);
      ctx.quadraticCurveTo(sx + 10, sy - 6, sx - 40, sy - 8);
      ctx.quadraticCurveTo(sx - 60, sy - 9, sx - 58, sy - 14);
      ctx.stroke();
      ctx.restore();
      K.dot(C[0], C[1] - 2, 3);
      // setback: seat-tube axis carried up to the rails, offset to the clamp
      const pd = P(t, 3.0, 3.6), axis = [T[0] + dx * ((T[1] - sy) / -dy), sy];
      ctx.save(); K.lw(1); K.alpha(0.6 * pd); ctx.setLineDash([K.px(3), K.px(3)]); K.line(T[0], T[1], axis[0], axis[1] - 10); ctx.restore();
      K.dim(axis[0] - back - (axis[0] - C[0]), sy + 14, axis[0], sy + 14, pd, ORANGE);
      K.label(axis[0] + 8, sy + 14, 'SETBACK 25 MM', P(t, 3.2, 3.6), 14, 'left');
      const pg = P(t, 3.2, 3.8), g = at(T, 6, 10);
      ctx.save(); K.lw(1); K.alpha(pg); K.line(g[0], g[1], g[0] + 20, g[1], pg); ctx.restore();
      K.tag(g[0] + 20, g[1], 'Ø 27.2', pg);
    }),

    /* 7. Brakes: the rotor spins in, the caliper clamps, it slows to a stop. */
    brakes: scene((ctx, t, K) => {
      const c = [430, 80], r = 56;
      const spin = 3 * Math.PI * P(t, 0, 2.8);
      K.ring(c[0], c[1], r, P(t, 0, 0.8)); K.ring(c[0], c[1], 44, P(t, 0.1, 0.9));
      ctx.save(); ctx.translate(c[0], c[1]); ctx.rotate(spin);
      K.alpha(P(t, 0.3, 0.9));
      for (let i = 0; i < 18; i++) { const a = (i * TAU) / 18; K.ring(Math.cos(a) * 50, Math.sin(a) * 50, 2.2); }
      for (let i = 0; i < 6; i++) { const a = (i * TAU) / 6; K.line(Math.cos(a) * 15, Math.sin(a) * 15, Math.cos(a + 0.35) * 44, Math.sin(a + 0.35) * 44); K.ring(Math.cos(a) * 11, Math.sin(a) * 11, 1.6); }
      K.ring(0, 0, 15);
      ctx.restore();
      // caliper over the top-rear of the rotor; pads turn orange when it bites
      const a0 = -2.55, a1 = -1.75, pc = P(t, 0.6, 1.2), bite = P(t, 1.5, 1.8);
      ctx.save(); K.alpha(pc);
      ctx.beginPath(); ctx.arc(c[0], c[1], r + 10, a0, a1); ctx.arc(c[0], c[1], 38, a1, a0, true); ctx.closePath();
      ctx.fillStyle = SLATE; ctx.fill(); ctx.stroke();
      ctx.strokeStyle = bite > 0 ? ORANGE : BONE; K.lw(2.4);
      ctx.beginPath(); ctx.arc(c[0], c[1], 50, a0 + 0.12, a1 - 0.12); ctx.stroke();
      ctx.restore();
      K.dot(c[0], c[1], 3.5);
      const pd = P(t, 3.0, 3.6), dimY = 12;
      K.ext(c[0] - r, c[1], c[0] - r, dimY - 4, pd); K.ext(c[0] + r, c[1], c[0] + r, dimY - 4, pd);
      K.dim(c[0] - r, dimY, c[0] + r, dimY, pd, ORANGE);
      K.label(c[0], dimY, 'Ø 160 MM', P(t, 3.2, 3.6), 14);
      const pt = P(t, 3.2, 3.8);
      ctx.save(); K.lw(1); K.alpha(pt); K.line(c[0] + r + 4, c[1], c[0] + r + 16, c[1], pt); ctx.restore();
      K.tag(c[0] + r + 16, c[1], '6-BOLT', pt);
    }),

    /* 8. Frame geometry: a 56 cm road bike from real numbers (mm, origin at the bottom bracket):
       stack 570, reach 390, 73° head and seat angles, 410 chainstays, 70 BB drop, 990 wheelbase,
       700x25 wheels (r 336). Wheels draw in, the frame builds tube by tube, then the parts,
       then stack and reach. */
    frame: scene((ctx, t, K) => {
      const s = 0.145, ox = 426, gy = 148, R = 336;
      const at = (x, y) => [ox + x * s, gy - (y + R - 70) * s]; // axles sit 70 above the BB
      const rad = (d) => (d * Math.PI) / 180;
      const BB = at(0, 0), RA = at(-404, 70), FA = at(586, 70), HT = at(390, 570);
      const HB = at(390 + 160 * Math.cos(rad(73)), 570 - 160 * Math.sin(rad(73)));
      const ST = at(-540 * Math.cos(rad(73.5)), 540 * Math.sin(rad(73.5)));
      const SS = at(-536 * Math.cos(rad(73.5)), 505);
      const crown = at(390 + 210 * Math.cos(rad(73)), 570 - 210 * Math.sin(rad(73)));
      const pw = P(t, 0, 1.1);
      wheel(ctx, K, RA[0], RA[1], R * s, 0, false, pw);
      wheel(ctx, K, FA[0], FA[1], R * s, 0, false, pw);
      const tubes = [
        [[BB, RA], 4], [[BB, ST], 6.5], [[SS, RA], 3.5], [[BB, HB], 8], [[ST, HT], 6], [[HT, HB], 8.5],
        [[HB, crown, [crown[0] + 3, crown[1] + 26], FA], 4.5],
      ];
      tubes.forEach(([pts, w], i) => K.tube(pts, w, P(t, 0.5 + i * 0.27, 0.95 + i * 0.27)));
      // parts: seatpost + saddle, stem + drop bar, crankset
      const pp = P(t, 2.4, 3.0);
      if (pp > 0) {
        ctx.save(); K.alpha(pp);
        const SP = at(-700 * Math.cos(rad(73.5)), 700 * Math.sin(rad(73.5)));
        K.line(ST[0], ST[1], SP[0], SP[1]);
        ctx.beginPath(); ctx.moveTo(SP[0] - 20, SP[1] - 3);
        ctx.quadraticCurveTo(SP[0] - 6, SP[1] - 7, SP[0] + 22, SP[1] - 4); ctx.lineTo(SP[0] + 18, SP[1] - 1);
        ctx.quadraticCurveTo(SP[0], SP[1] - 1, SP[0] - 20, SP[1] - 3); ctx.stroke();
        const steer = at(390 - 25 * Math.cos(rad(73)), 570 + 25 * Math.sin(rad(73)));
        const clamp = [steer[0] + 15, steer[1] - 1];
        K.line(steer[0], steer[1], clamp[0], clamp[1]); K.line(HT[0], HT[1], steer[0], steer[1]);
        ctx.beginPath(); ctx.moveTo(clamp[0], clamp[1]);
        ctx.bezierCurveTo(clamp[0] + 14, clamp[1], clamp[0] + 15, clamp[1] + 14, clamp[0] + 9, clamp[1] + 19);
        ctx.quadraticCurveTo(clamp[0] + 5, clamp[1] + 22, clamp[0] - 1, clamp[1] + 21); ctx.stroke();
        K.ring(BB[0], BB[1], 14.5); K.ring(BB[0], BB[1], 3);
        const ca = rad(55), CE = [BB[0] + Math.cos(ca) * 25, BB[1] + Math.sin(ca) * 25];
        K.line(BB[0], BB[1], CE[0], CE[1]); K.line(CE[0] - 5, CE[1], CE[0] + 5, CE[1]);
        ctx.restore();
      }
      const pd = P(t, 3.0, 3.6);
      if (pd > 0) { K.dot(BB[0], BB[1], 3.5); K.dot(HT[0], HT[1], 3.5); }
      const ry = HT[1] - 14;
      K.ext(BB[0], BB[1] - 6, BB[0], ry - 4, pd); K.ext(HT[0], HT[1] - 4, HT[0], ry - 4, pd);
      K.dim(BB[0], ry, HT[0], ry, pd, ORANGE);
      K.label((BB[0] + HT[0]) / 2, ry, 'REACH', P(t, 3.2, 3.6), 14);
      const sx = FA[0] + R * s + 12;
      K.ext(BB[0] + 16, BB[1], sx + 4, BB[1], pd); K.ext(HT[0] + 8, HT[1], sx + 4, HT[1], pd);
      K.dim(sx, BB[1], sx, HT[1], pd, ORANGE);
      K.label(sx + 8, (BB[1] + HT[1]) / 2, 'STACK', P(t, 3.2, 3.6), 14, 'left');
    }),

    /* ---------- Generic scenes for collections without a part scene ---------- */

    /* 9. Elevation profile: a climb draws in, a rider dot rides it to the summit. */
    profile: scene((ctx, t, K) => {
      const base = 136, x0 = 210, x1 = 590, top = 490;
      const yAt = (x) => {
        const up = x <= top ? Math.pow((x - x0) / (top - x0), 1.6) : 1 - 0.55 * ((x - top) / (x1 - top)) ** 1.3;
        return base - 104 * up + 4 * Math.sin(x / 13) * (x <= top ? 1 : 0.5);
      };
      const pts = []; for (let x = x0; x <= x1; x += 4) pts.push([x, yAt(x)]);
      const pd = P(t, 0, 1.6, (x) => x);
      ctx.save(); K.alpha(0.06 * pd); ctx.beginPath(); ctx.moveTo(x0, base);
      pts.forEach(([x, y]) => { if (x <= x0 + (x1 - x0) * pd) ctx.lineTo(x, y); });
      ctx.lineTo(x0 + (x1 - x0) * pd, base); ctx.closePath(); ctx.fill(); ctx.restore();
      K.poly(pts, pd);
      ctx.save(); K.lw(1); K.alpha(0.5);
      K.line(x0, base, x1, base, P(t, 0, 1.4));
      for (let x = x0; x <= x1; x += 38) if (x <= x0 + (x1 - x0) * P(t, 0.2, 1.6)) K.line(x, base, x, base + 5);
      ctx.restore();
      const pr = P(t, 1.4, 3.0, easeInOut), rx = x0 + (top - x0) * pr;
      if (pr > 0) {
        ctx.save(); ctx.strokeStyle = ORANGE; K.lw(2.2); K.poly(pts.filter(([x]) => x <= rx).concat([[rx, yAt(rx)]])); ctx.restore();
        K.dot(rx, yAt(rx), 4.5);
      }
      const pc = P(t, 3.0, 3.6), gx = 400, gy = yAt(gx);
      ctx.save(); K.lw(1); K.alpha(0.8 * pc); K.poly([[gx - 34, yAt(gx - 34) + 10], [gx, yAt(gx - 34) + 10], [gx, gy + 10]]); ctx.restore();
      K.label(gx + 6, yAt(gx - 34) + 20, '8%', P(t, 3.1, 3.6), 14, 'left');
      ctx.save(); K.lw(1); K.alpha(pc); K.line(top + 6, yAt(top), top + 18, yAt(top), pc); ctx.restore();
      K.tag(top + 18, yAt(top), 'SUMMIT', pc);
    }),

    /* 10. Topo route: contours fade in, a route draws from start to finish. */
    route: scene((ctx, t, K) => {
      const cx = 450, cy = 76;
      ctx.save(); K.lw(1);
      for (let i = 0; i < 6; i++) {
        K.alpha(0.28 * P(t, i * 0.12, 0.6 + i * 0.12));
        const r = 14 + i * 13;
        ctx.beginPath();
        for (let a = 0; a <= TAU + 0.01; a += 0.12) {
          const w = 1 + 0.12 * Math.sin(a * 3 + i) + 0.07 * Math.sin(a * 5 - i * 2);
          const x = cx + Math.cos(a) * r * 1.9 * w, y = cy + Math.sin(a) * r * w;
          a === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      ctx.restore();
      const route = [[262, 124], [300, 118], [330, 98], [372, 104], [404, 82], [440, 88], [470, 62], [512, 58], [536, 38], [562, 34]];
      const pr = P(t, 1.0, 2.9, easeInOut);
      ctx.save(); ctx.strokeStyle = ORANGE; K.lw(2.2); ctx.setLineDash([K.px(6), K.px(4)]); K.poly(route, pr); ctx.restore();
      const ps = P(t, 0.8, 1.2);
      if (ps > 0) { ctx.save(); K.alpha(ps); K.ring(262, 124, 5); ctx.restore(); }
      const pf = P(t, 2.8, 3.1);
      if (pf > 0) {
        ctx.save(); K.alpha(pf); K.line(562, 34, 562, 14); ctx.fillStyle = ORANGE;
        ctx.beginPath(); ctx.moveTo(562, 14); ctx.lineTo(578, 19); ctx.lineTo(562, 24); ctx.closePath(); ctx.fill(); ctx.restore();
        K.dot(562, 34, 3.5);
      }
      K.label(262, 140, 'START', P(t, 1.0, 1.4), 12);
      K.label(562, 46, 'FINISH', P(t, 3.0, 3.4), 12);
      K.tag(380, 128, '62 KM', P(t, 3.1, 3.7));
    }),

    /* 11. Bike computer: the head unit draws, its fields count up, a track draws on the map. */
    computer: scene((ctx, t, K) => {
      const x = 336, y = 14, w = 124, h = 122;
      const pb = P(t, 0, 0.9);
      ctx.save(); K.alpha(pb);
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 14); ctx.stroke();
      ctx.beginPath(); ctx.roundRect(x + 9, y + 10, w - 18, h - 30, 6); ctx.stroke();
      K.line(x + w / 2 - 14, y + h - 10, x + w / 2 + 14, y + h - 10);
      K.line(x + w, y + 30, x + w, y + 44); K.line(x, y + 30, x, y + 44);
      ctx.restore();
      const pc = P(t, 0.9, 3.0, easeInOut);
      const rows = [['SPEED', (38.4 * pc).toFixed(1), 'KM/H'], ['POWER', String(Math.round(265 * pc)), 'W'], ['HR', String(Math.round(60 + 92 * pc)), 'BPM']];
      ctx.save(); K.alpha(P(t, 0.8, 1.2)); ctx.textBaseline = 'alphabetic';
      rows.forEach(([lab, val, unit], i) => {
        const ry = y + 28 + i * 19;
        ctx.textAlign = 'left'; K.font(10); ctx.fillStyle = 'rgba(231, 230, 221, 0.65)'; ctx.fillText(lab, x + 16, ry);
        ctx.textAlign = 'right'; K.font(17); ctx.fillStyle = i === 1 ? ORANGE : BONE; ctx.fillText(val, x + w - 34, ry + 2);
        K.font(10); ctx.fillStyle = 'rgba(231, 230, 221, 0.65)'; ctx.fillText(unit, x + w - 14, ry + 2);
        ctx.save(); ctx.strokeStyle = 'rgba(231, 230, 221, 0.18)'; K.lw(1); K.line(x + 14, ry + 6, x + w - 14, ry + 6); ctx.restore();
      });
      ctx.restore();
      const track = [[352, 104], [362, 99], [368, 102], [378, 95], [390, 97], [396, 91], [410, 94], [422, 88], [438, 91]];
      ctx.save(); ctx.strokeStyle = ORANGE; K.lw(1.6); K.poly(track, P(t, 1.2, 3.0)); ctx.restore();
      const pt = P(t, 3.0, 3.6);
      ctx.save(); K.lw(1); K.alpha(pt); K.line(x + w + 4, y + 52, x + w + 18, y + 52, pt); ctx.restore();
      K.tag(x + w + 18, y + 52, 'GPS · ANT+ · BLE', pt);
    }),

    /* 12. Jersey (back view): outline draws seam by seam, then the three rear pockets. */
    jersey: scene((ctx, t, K) => {
      const c = 440;
      const outline = [[c - 16, 16], [c - 46, 24], [c - 74, 52], [c - 60, 66], [c - 44, 54], [c - 46, 136], [c + 46, 136], [c + 44, 54], [c + 60, 66], [c + 74, 52], [c + 46, 24], [c + 16, 16]];
      K.poly(outline, P(t, 0, 1.6, (x) => x));
      ctx.save(); K.alpha(P(t, 1.4, 1.8));
      ctx.beginPath(); ctx.moveTo(c - 16, 16); ctx.quadraticCurveTo(c, 24, c + 16, 16); ctx.stroke();
      K.lw(0.8); K.alpha(0.6 * P(t, 1.6, 2.0)); K.line(c - 46, 24, c - 44, 52); K.line(c + 46, 24, c + 44, 52);
      ctx.restore();
      const pp = P(t, 1.9, 2.7);
      ctx.save(); K.lw(1.2);
      [-1, 0, 1].forEach((i, n) => { K.alpha(P(t, 1.9 + n * 0.15, 2.4 + n * 0.15)); ctx.beginPath(); ctx.roundRect(c + i * 27 - 12.5, 100, 25, 30, 3); ctx.stroke(); });
      ctx.restore();
      ctx.save(); K.alpha(pp); K.lw(1); ctx.setLineDash([K.px(3), K.px(3)]); K.line(c - 43, 94, c + 43, 94); ctx.restore();
      const pt = P(t, 2.8, 3.2);
      if (pt > 0) { ctx.save(); K.alpha(pt); ctx.fillStyle = ORANGE; ctx.fillRect(c - 5, 22, 10, 8); ctx.restore(); }
      K.label(c, 40, 'M', pt, 12);
      const pc = P(t, 3.0, 3.6);
      ctx.save(); K.lw(1); K.alpha(pc); K.line(c + 40, 115, c + 62, 115, pc); ctx.restore();
      K.tag(c + 62, 115, '3 POCKETS', pc);
    }),

    /* 13. Helmet (side view): shell draws, vents cut in, the fit dial turns. */
    helmet: scene((ctx, t, K) => {
      // Road helmet, side view, facing right: egg-shaped shell with a swept rear tail and a
      // rounded brow, rows of front-to-back vent channels, the liner inside, retention dial and
      // Y-straps underneath.
      const shell = () => {
        ctx.beginPath();
        ctx.moveTo(384, 106);
        ctx.bezierCurveTo(392, 70, 420, 38, 460, 30);
        ctx.bezierCurveTo(500, 22, 538, 40, 547, 70);
        ctx.bezierCurveTo(551, 84, 545, 96, 532, 100);
        ctx.bezierCurveTo(510, 97, 490, 91, 470, 94);
        ctx.bezierCurveTo(450, 97, 436, 102, 420, 109);
        ctx.bezierCurveTo(406, 113, 392, 112, 384, 106);
        ctx.closePath();
      };
      const ps = P(t, 0, 1.3);
      ctx.save(); ctx.beginPath(); ctx.rect(376, 14, 180 * ps, 104); ctx.clip();
      shell(); ctx.fillStyle = SLATE; ctx.fill(); K.lw(1.6); ctx.stroke();
      ctx.restore();
      // vent channels, running front to back, row by row
      const rows = [
        [[[430, 41], [452, 34]], [[464, 31], [488, 29]], [[500, 30], [520, 38]]],
        [[[414, 60], [436, 50]], [[449, 46], [474, 43]], [[487, 43], [510, 48]], [[521, 54], [534, 64]]],
        [[[424, 76], [444, 68]], [[458, 64], [482, 62]], [[495, 63], [515, 70]]],
      ];
      let n = 0;
      rows.forEach((row) => row.forEach((pts) => { K.tube(pts, 4.5, P(t, 1.2 + n * 0.08, 1.6 + n * 0.08)); n++; }));
      ctx.save(); K.alpha(P(t, 2.0, 2.3)); ctx.beginPath(); ctx.roundRect(522, 82, 16, 5, 2.5); ctx.stroke(); ctx.restore();
      // the liner, inside the shell
      ctx.save(); K.alpha(P(t, 2.0, 2.4)); K.lw(1); ctx.setLineDash([K.px(2), K.px(3)]); ctx.strokeStyle = ORANGE;
      ctx.beginPath(); ctx.moveTo(398, 102); ctx.bezierCurveTo(404, 72, 428, 46, 462, 40); ctx.bezierCurveTo(498, 34, 528, 50, 536, 74); ctx.bezierCurveTo(539, 84, 536, 90, 530, 93); ctx.stroke();
      ctx.restore();
      // retention dial at the back, Y-straps to the buckle
      const pr = P(t, 2.1, 2.5), spin = 2.4 * P(t, 2.4, 3.0);
      if (pr > 0) {
        ctx.save(); K.alpha(pr); K.lw(1);
        ctx.beginPath(); ctx.moveTo(410, 110); ctx.quadraticCurveTo(404, 116, 406, 121); ctx.stroke();
        ctx.translate(406, 125); K.lw(1.4); K.ring(0, 0, 5.5); ctx.rotate(spin);
        for (let i = 0; i < 8; i++) { const a = (i * TAU) / 8; K.line(Math.cos(a) * 3.5, Math.sin(a) * 3.5, Math.cos(a) * 5.5, Math.sin(a) * 5.5); }
        ctx.restore();
        ctx.save(); K.alpha(0.85 * pr); K.lw(1);
        K.poly([[446, 100], [474, 126], [504, 95]]); K.line(474, 126, 475, 135);
        ctx.beginPath(); ctx.roundRect(468, 135, 14, 6, 2); ctx.stroke();
        ctx.restore();
      }
      const pc = P(t, 3.0, 3.6);
      ctx.save(); K.lw(1); K.alpha(pc); K.line(402, 72, 380, 72, pc); ctx.restore();
      K.tag(380, 72, 'ROTATIONAL LINER', pc, 'right');
    }),

    /* 14. Shoe and cleat: the upper draws, the dial tightens the wires, the cleat clicks on. */
    shoe: scene((ctx, t, K) => {
      const pu = P(t, 0, 1.4, (x) => x);
      const sole = [[356, 118], [420, 121], [482, 121], [530, 114]];
      const upper = [[530, 114], [526, 104], [500, 94], [462, 78], [432, 66], [412, 69], [392, 62], [370, 58], [356, 70], [352, 96], [356, 118]];
      K.poly(sole, pu); K.poly(upper, pu);
      ctx.save(); K.lw(2.6); K.alpha(P(t, 0.8, 1.2)); K.poly(sole.map(([x, y]) => [x, y + 3])); ctx.restore();
      const tight = P(t, 1.4, 2.4);
      ctx.save(); K.lw(0.9); K.alpha(P(t, 1.2, 1.6));
      for (let i = 0; i < 4; i++) {
        const x = 432 + i * 16, sag = 6 * (1 - tight);
        ctx.beginPath(); ctx.moveTo(x - 10, 74 + i * 6); ctx.quadraticCurveTo(x, 84 + i * 6 + sag, x + 10, 74 + i * 6 + 4); ctx.stroke();
      }
      ctx.restore();
      const pd = P(t, 1.2, 1.6);
      if (pd > 0) {
        ctx.save(); K.alpha(pd); ctx.translate(420, 82); K.ring(0, 0, 9); ctx.rotate(3 * tight);
        for (let i = 0; i < 10; i++) { const a = (i * TAU) / 10; K.line(Math.cos(a) * 6, Math.sin(a) * 6, Math.cos(a) * 9, Math.sin(a) * 9); }
        ctx.restore(); K.dot(420, 82, 2.5);
      }
      const drop = 22 * (1 - P(t, 2.4, 2.8)), pc = P(t, 2.3, 2.6), click = t >= 2.8;
      if (pc > 0) {
        ctx.save(); K.alpha(pc); ctx.strokeStyle = click ? ORANGE : BONE; K.lw(1.6);
        ctx.beginPath(); ctx.moveTo(466, 126 + drop); ctx.lineTo(500, 126 + drop); ctx.lineTo(492, 134 + drop); ctx.lineTo(474, 134 + drop); ctx.closePath(); ctx.stroke();
        ctx.restore();
      }
      const pt = P(t, 3.0, 3.6);
      ctx.save(); K.lw(1); K.alpha(pt); K.line(502, 130, 532, 130, pt); ctx.restore();
      K.tag(532, 130, '3-BOLT', pt);
    }),

    /* 15. Workshop: hex keys fan out, the torque wrench clicks, a drop of lube falls. */
    workshop: scene((ctx, t, K) => {
      ctx.save();
      [0, 1, 2].forEach((i) => {
        const a = -0.5 + i * 0.32 * P(t, 0.2, 1.0), len = 54 - i * 10;
        K.alpha(P(t, i * 0.12, 0.5 + i * 0.12));
        ctx.save(); ctx.translate(260, 118); ctx.rotate(a);
        K.tube([[0, 0], [0, -len], [13 - i * 2, -len]], 4.5 - i);
        ctx.restore();
      });
      ctx.restore();
      const bolt = [360, 100], ang = -0.16 * (1 - P(t, 1.2, 2.4, easeInOut));
      const pw = P(t, 0.4, 1.2);
      ctx.save(); K.alpha(pw);
      ctx.beginPath(); for (let i = 0; i <= 6; i++) { const a = (i * TAU) / 6; const x = bolt[0] + Math.cos(a) * 7, y = bolt[1] + Math.sin(a) * 7; i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); } ctx.stroke();
      ctx.translate(bolt[0], bolt[1]); ctx.rotate(ang);
      K.ring(0, 0, 13);
      ctx.beginPath(); ctx.roundRect(12, -6, 160, 12, 6); ctx.stroke();
      ctx.beginPath(); ctx.roundRect(122, -9, 50, 18, 8); ctx.stroke();
      for (let i = 0; i < 6; i++) K.line(70 + i * 8, -6, 70 + i * 8, i % 2 ? -2 : 0);
      ctx.restore();
      const clicked = t >= 2.4;
      if (clicked) {
        const pc = P(t, 2.4, 2.8);
        ctx.save(); ctx.strokeStyle = ORANGE; K.lw(1.6); K.alpha(1 - 0.6 * pc);
        ctx.beginPath(); ctx.arc(bolt[0], bolt[1], 20 + 6 * pc, -1.2, -0.4); ctx.stroke(); ctx.restore();
        K.dot(bolt[0] + 94, bolt[1] - 2, 3);
      }
      // lube bottle and the drop
      const pb = P(t, 0.6, 1.2);
      ctx.save(); K.alpha(pb);
      ctx.beginPath(); ctx.roundRect(552, 8, 22, 40, 5); ctx.stroke();
      K.poly([[556, 48], [560, 60], [566, 60], [570, 48]]); K.line(563, 60, 563, 66);
      ctx.restore();
      const pdp = P(t, 2.6, 3.2, (x) => x * x);
      if (t >= 2.6) {
        const dy = 70 + 58 * pdp;
        ctx.save(); ctx.fillStyle = ORANGE; K.alpha(pdp < 1 ? 1 : 0.9);
        ctx.beginPath(); ctx.arc(563, dy, 3.2, 0, TAU); ctx.moveTo(560.2, dy - 1.5); ctx.lineTo(563, dy - 7); ctx.lineTo(565.8, dy - 1.5); ctx.fill(); ctx.restore();
      }
      ctx.save(); K.lw(3); K.alpha(0.5 * P(t, 0.6, 1.2)); ctx.setLineDash([K.px(3), K.px(2)]); K.line(520, 132, 600, 132); ctx.restore();
      K.label(bolt[0] + 94, bolt[1] + 22, '5 NM', P(t, 3.0, 3.5), 14);
    }),

    /* 16. Buy · Sell · Trade: a part circles inspect → photograph → list and gets tagged. */
    trade: scene((ctx, t, K) => {
      const c = [440, 68], r = 44;
      const steps = [['INSPECT', -Math.PI / 2], ['PHOTOGRAPH', Math.PI / 6], ['LIST', (5 * Math.PI) / 6]];
      const pl = P(t, 0, 1.0);
      ctx.save(); K.alpha(0.7); K.lw(1.2); ctx.setLineDash([K.px(4), K.px(4)]); K.ring(c[0], c[1], r, pl); ctx.restore();
      const pa = P(t, 0.6, 2.8, easeInOut), ang = -Math.PI / 2 + TAU * pa;
      steps.forEach(([name, a], i) => {
        const x = c[0] + Math.cos(a) * r, y = c[1] + Math.sin(a) * r;
        const passed = pa * TAU >= ((a + Math.PI / 2 + TAU) % TAU) - 0.01 && t > 0.6;
        ctx.save(); K.alpha(P(t, 0.3 + i * 0.15, 0.7 + i * 0.15)); ctx.fillStyle = SLATE;
        ctx.beginPath(); ctx.arc(x, y, K.px(6), 0, TAU); ctx.fill(); ctx.strokeStyle = passed ? ORANGE : BONE; ctx.stroke(); ctx.restore();
        const lx = i === 0 ? x : i === 1 ? x + 12 : x - 12, ly = i === 0 ? y - 14 : y + 4;
        K.label(lx, ly, name, P(t, 0.5 + i * 0.15, 0.9 + i * 0.15), 11, i === 0 ? 'center' : i === 1 ? 'left' : 'right');
      });
      if (t < 2.9) {
        const x = c[0] + Math.cos(ang) * r, y = c[1] + Math.sin(ang) * r;
        ctx.save(); K.alpha(P(t, 0.5, 0.8)); ctx.translate(x, y); ctx.rotate(ang * 2); ctx.fillStyle = SLATE;
        ctx.beginPath(); ctx.arc(0, 0, 10, 0, TAU); ctx.fill(); K.ring(0, 0, 10); K.ring(0, 0, 3);
        for (let i = 0; i < 8; i++) { const a = (i * TAU) / 8; K.line(Math.cos(a) * 10, Math.sin(a) * 10, Math.cos(a) * 13, Math.sin(a) * 13); }
        ctx.restore();
      }
      const ps = P(t, 2.8, 3.2);
      if (ps > 0) {
        ctx.save(); ctx.translate(c[0], c[1]); ctx.rotate(-0.12); ctx.scale(1.3 - 0.3 * ps, 1.3 - 0.3 * ps); K.alpha(ps);
        K.font(15); const w = ctx.measureText('INSPECTED').width + K.px(16), h = K.px(24);
        ctx.strokeStyle = ORANGE; K.lw(1.6); ctx.strokeRect(-w / 2, -h / 2, w, h);
        ctx.fillStyle = ORANGE; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText('INSPECTED', 0, K.px(1));
        ctx.restore();
      }
      const pw = P(t, 3.1, 3.7);
      if (pw > 0) {
        ctx.save(); K.alpha(pw); K.font(12); ctx.fillStyle = BONE; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        if ('letterSpacing' in ctx) ctx.letterSpacing = `${K.px(4)}px`;
        ctx.fillText('CYCLING UPGRADES', c[0], 140); ctx.restore();
      }
    }),
  };

  window.CuMotion = { scenes };
})();
